import type { MarketedMembershipPlanType } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import { formatCurrencyNatural } from '@frontend/duck-tape';
import type { SubscriptionPlan } from '@frontend/react-domain';
import { usePromotionTranslation } from '@frontend/react-domain';
import { Button, Divider, Icon, LinkButton, Text, TextInput, XStack, YStack } from '@frontend/web-react';
import { CalloutBadge } from '@frontend/web-react/components/Primitives/Badges';
import { safeCss } from '@frontend/web-utils';
import { usePricingTerminology } from '@frontend/web/context';
import { getValidatedPromoInput, useLazyRetrievePromotionDetailsQuery, useMembershipPlans } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useRef, useState } from 'react';
import { SkeletonPlanCard } from '../../Paywall/SkeletonPlanCard';

export type SelectSubscriptionPlanProps = {
  onContinue: (planType: MarketedMembershipPlanType, promotion?: SubscriptionPlan['promotion']) => void;
  subscriptionPlan?: SubscriptionPlan;
};

export const SelectSubscriptionPlan = ({ onContinue, subscriptionPlan }: SelectSubscriptionPlanProps) => {
  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const [promoCodeInput, setPromoCodeInput] = useState(subscriptionPlan?.promotion?.promoCode ?? '');
  const [selectedPlanType, setSelectedPlanType] = useState<MarketedMembershipPlanType | undefined>(
    subscriptionPlan?.planType as MarketedMembershipPlanType,
  );

  const { pricingTerminology, showCredits } = usePricingTerminology();

  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();
  const {
    actions: { handleApplyPromoCode, resetPromoCode },
    state: { isApplyPromoCodeLoading, promoCode, promoCodeErrorMessage, promotionDescription, promotionDetails },
  } = usePromotionTranslation({
    defaultPromoCode: subscriptionPlan?.promotion?.promoCode,
    firstPurchaseData: undefined,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const validatedPromo = getValidatedPromoInput({
    promoCode,
    promotionId: promotionDetails?.promotionId,
  });

  const { displayPlanLookup, displaySelectablePlans, isError, isLoading, pricingState, refetch } = useMembershipPlans({
    validatedPromo,
  });
  const selectedPlanData = selectedPlanType ? displayPlanLookup[selectedPlanType] : undefined;
  const selectedPlanPricing = getMembershipPlanDisplayPricing(selectedPlanData);

  const price = selectedPlanPricing?.primaryPrice;

  const hasPromotion = Boolean(promotionDetails && selectedPlanType);
  const isPricingTransitioning = pricingState === 'transitioning';

  const handleContinue = () => {
    if (!selectedPlanType) {
      return;
    }
    const promotion =
      promoCode && promotionDescription && promotionDetails
        ? { promoCode, promotionDescription, promotionDetails }
        : undefined;
    onContinue(selectedPlanType, promotion);
  };

  if (isLoading) {
    return (
      <YStack accessibilityLabel="Loading subscription plans" className={safeCss('py-md')} gap="md" p="md">
        <YStack gap="xs">
          <YStack bg="lineGray" height={24} radius="md" width="60%" />
          <YStack bg="lineGray" height={16} radius="md" width="80%" />
        </YStack>
        <YStack gap="md">
          <SkeletonPlanCard />
          <SkeletonPlanCard />
          <SkeletonPlanCard />
        </YStack>
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack
        accessibilityLabel="Error loading subscription plans"
        alignItems="center"
        className={safeCss('py-md')}
        gap="md"
        justifyContent="center"
        p="md"
      >
        <Icon className="text-red-500" name="IconAlertCircle" size={48} />
        <Text type="h4">Your plans took a duck dive.</Text>
        <Text color="secondary" type="p-sm">
          {"Don't worry, we'll bring them back. Try again."}
        </Text>
        <Button label="Try again" onClick={() => refetch()} size="md" variant="primary" />
      </YStack>
    );
  }

  return (
    <YStack accessibilityLabel="Select subscription plan" className={safeCss('py-md')} gap="md" p="md">
      <YStack gap="xs">
        <Text type="h4">Pick your membership plan</Text>
        <Text color="secondary" type="p-sm">
          {hasPromotion
            ? 'Your subscription will renew on your selected plan after the promotion period.'
            : 'Your subscription will renew monthly on your selected plan.'}
        </Text>
      </YStack>

      <YStack gap="md" role="radiogroup">
        {displaySelectablePlans.map((plan) => {
          const isSelected = selectedPlanType === plan.planType;
          const planPricing = getMembershipPlanDisplayPricing(plan);

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedPlanType(plan.planType);
            }
          };

          return (
            <XStack
              accessibilityLabel={`Select ${plan.label} plan`}
              alignItems="center"
              aria-checked={isSelected}
              bg="white"
              borderColor={isSelected ? 'borderSecondary' : 'borderPrimary'}
              borderWidth={2}
              className="cursor-pointer hover:border-borderSecondary"
              gapX="lg"
              key={plan.planType}
              onClick={() => setSelectedPlanType(plan.planType)}
              onKeyDown={handleKeyDown}
              p="md"
              px="lg"
              radius="lg"
              role="radio"
              tabIndex={0}
            >
              <Icon name={isSelected ? 'IconCircleCheckFilled' : 'IconCircle'} size={24} />

              <YStack flex={1} gapY="xs">
                <XStack alignItems="center" gapX="md">
                  <Text type="h4">{plan.label}</Text>
                  {plan.planCode === 'HOUSEHOLD' ? <CalloutBadge>MOST POPULAR</CalloutBadge> : null}
                </XStack>
                <XStack alignItems="center" gapX="xs">
                  {isPricingTransitioning ? (
                    <YStack gapY="xs">
                      <YStack bg="lineGray" height={24} radius="md" width={88} />
                      <YStack bg="lineGray" height={16} radius="md" width={64} />
                    </YStack>
                  ) : (
                    <>
                      <Text className="leading-6" type="h2">
                        {formatCurrencyNatural(planPricing?.primaryPrice ?? plan.price)}
                      </Text>
                      <Text className="leading-4" type="p-md">
                        /mo
                      </Text>
                    </>
                  )}
                </XStack>
                {!isPricingTransitioning && planPricing?.secondaryPrice ? (
                  <Text className="line-through" color="tertiary" type="p-sm">
                    {formatCurrencyNatural(planPricing.secondaryPrice)}/mo
                  </Text>
                ) : null}
              </YStack>

              <YStack className="text-right">
                {showCredits ? (
                  <Text color="primary" type="p-sm">
                    {plan.creditsPerMonth} {pricingTerminology.unit_term_plural}
                  </Text>
                ) : null}
                <Text color="primary" type="p-sm">
                  ~ {plan.estimatedTasksPerMonth.min} - {plan.estimatedTasksPerMonth.max} tasks
                </Text>
              </YStack>
            </XStack>
          );
        })}
      </YStack>

      <YStack>
        <Text className="italic" type="caption">
          All plans include expert copilots to support you every step of the way; smart automation that anticipates and
          streamlines your tasks; exclusive, member-only perks and offers; and seamless syncing for effortless task
          management across devices.
        </Text>
      </YStack>

      {promotionDescription ? (
        <YStack gap="sm">
          <Divider />
          <YStack gap="xs">
            <Text type="h6">Promotion</Text>
            <XStack
              alignItems="center"
              bg="white"
              borderColor="borderPrimary"
              borderWidth={2}
              gapX="md"
              p="lg"
              radius="lg"
            >
              <YStack className="flex-1">
                <Text type="h5">{promoCode ? `Promo code: ${promoCode}` : 'Promo applied'}</Text>
                <Text className="italic" color="secondary" type="p-sm">
                  {promotionDescription}
                </Text>
              </YStack>
              <Icon
                accessibilityLabel="Remove promo code"
                canFocus
                enableBackgroundColor={false}
                name="IconX"
                onClick={() => {
                  resetPromoCode();
                  setPromoCodeInput('');
                  promoCodeInputRef.current?.blur();
                }}
              />
            </XStack>
          </YStack>
        </YStack>
      ) : (
        <YStack gap="md">
          <Divider />
          <YStack gap="xs">
            <Text type="h6">Promo code</Text>
            <XStack>
              <YStack flex={1}>
                <TextInput
                  classNames={{ input: safeCss('rounded-md') }}
                  disabled={isApplyPromoCodeLoading}
                  error={promoCodeErrorMessage}
                  onChange={(val) => {
                    if (val === '') {
                      setPromoCodeInput('');
                    } else {
                      setPromoCodeInput(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && promoCodeInput.trim() && !isApplyPromoCodeLoading) {
                      const trimmedCode = promoCodeInput.trim();
                      handleApplyPromoCode(trimmedCode);
                      promoCodeInputRef.current?.blur();
                    }
                  }}
                  placeholder="Enter promo code here"
                  ref={promoCodeInputRef}
                  rightSection={
                    <LinkButton
                      accessibilityLabel="Apply promo code button"
                      className="mr-xxl"
                      disabled={!promoCodeInput.trim() || isApplyPromoCodeLoading}
                      isLoading={isApplyPromoCodeLoading}
                      label="Apply"
                      onClick={() => {
                        const trimmedCode = promoCodeInput.trim();
                        if (!trimmedCode) {
                          return;
                        }
                        handleApplyPromoCode(trimmedCode);
                        promoCodeInputRef.current?.blur();
                      }}
                      withUnderline={false}
                    />
                  }
                  value={promoCodeInput}
                />
              </YStack>
            </XStack>
          </YStack>
        </YStack>
      )}
      <XStack alignItems="center" gapX="md" justifyContent="between">
        {isPricingTransitioning && selectedPlanType ? (
          <XStack alignItems="center" gapX="sm">
            <Icon name="IconCoin" size={24} />
            <YStack gapY="xs">
              <YStack bg="lineGray" height={18} radius="md" width={144} />
              <YStack bg="lineGray" height={24} radius="md" width={96} />
            </YStack>
          </XStack>
        ) : null}
        {!isPricingTransitioning && hasPromotion && price !== undefined ? (
          <XStack alignItems="center" gapX="sm">
            <Icon name="IconCoin" size={24} />
            <XStack alignItems="center" gap="md">
              <Text type="h5">{hasPromotion ? `What you'll pay today` : 'Total to pay'}</Text>
              <Text className="italic font-serif" type="h3">
                {formatCurrencyNatural(price)}
              </Text>
            </XStack>
          </XStack>
        ) : null}
        <Button
          accessibilityLabel="Continue to payment"
          disabled={!selectedPlanData || isPricingTransitioning}
          fullWidth={!hasPromotion}
          label="Continue"
          onClick={handleContinue}
          size="lg"
          variant="primary"
        />
      </XStack>
    </YStack>
  );
};
